/**
 * useAnonymousBrief - aggregates project-brief context from anonymous
 * users across the marketing site & mini-matchers so it can be
 * (a) shown back to the user as "Pick up where you left off",
 * (b) consumed by CoPilot to personalise responses, and
 * (c) synced to the server-side user_briefs row once the user signs in.
 *
 * Storage shape (localStorage key `bp.anonymousBrief`):
 *   {
 *     v: 1,
 *     updatedAt: ISO-string,
 *     state: 'SA' | 'VIC' | ...
 *     region: 'Adelaide Metro' | ...
 *     suburb: 'Norwood',
 *     build_type: 'New Homes' | 'Renovations' | ...
 *     build_goal: 'forever' | 'invest' | ...
 *     budget: 625000,                           // midpoint
 *     budget_band: '$500K – $750K',
 *     bedrooms: 4,
 *     timing: 'asap' | 'planning' | ...
 *     priorities: ['Energy efficiency', ...],
 *     block_width: 12.5,
 *     // free-form bag for category-specific extras (e.g. tradie scope)
 *     extras: { tiler: {...}, supplier:windows_doors: {...} }
 *   }
 *
 * The hook intentionally NEVER touches PII (name/email/phone) - those
 * stay in the lead-gate flow only.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'bp.anonymousBrief';
const STORAGE_VERSION = 1;

const PII_KEYS = new Set([
  'contact_name', 'contact_email', 'contact_phone',
  'name', 'email', 'phone', 'mobile',
]);

const _emptyBrief = () => ({
  v: STORAGE_VERSION,
  updatedAt: null,
  state: null,
  region: null,
  suburb: null,
  build_type: null,
  build_goal: null,
  budget: null,
  budget_band: null,
  bedrooms: null,
  timing: null,
  priorities: [],
  block_width: null,
  extras: {},
});

const _read = () => {
  if (typeof window === 'undefined') return _emptyBrief();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return _emptyBrief();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || parsed.v !== STORAGE_VERSION) {
      return _emptyBrief();
    }
    return { ..._emptyBrief(), ...parsed };
  } catch {
    return _emptyBrief();
  }
};

const _write = (brief) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(brief));
    window.dispatchEvent(new CustomEvent('buildpilot:anonymous-brief-updated', { detail: brief }));
  } catch { /* ignore quota errors */ }
};

const _stripPII = (obj) => {
  if (!obj || typeof obj !== 'object') return {};
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (PII_KEYS.has(k)) return;
    out[k] = v;
  });
  return out;
};

export function useAnonymousBrief() {
  const [brief, setBrief] = useState(_read);

  // Cross-tab + cross-component sync
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setBrief(_read());
    };
    const onCustom = (e) => {
      setBrief(e.detail || _read());
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('buildpilot:anonymous-brief-updated', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('buildpilot:anonymous-brief-updated', onCustom);
    };
  }, []);

  /**
   * Merge a partial brief update into the stored brief. Scalar fields
   * overwrite, arrays merge (deduped), nested `extras` keys get spread.
   * PII keys are silently stripped before persisting.
   */
  const update = useCallback((partial, opts = {}) => {
    if (!partial || typeof partial !== 'object') return;
    const clean = _stripPII(partial);
    const current = _read();
    const next = { ...current };
    Object.entries(clean).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      if (k === 'priorities' && Array.isArray(v)) {
        const merged = Array.from(new Set([...(current.priorities || []), ...v]));
        next.priorities = merged.slice(0, 6);
      } else if (k === 'extras' && typeof v === 'object') {
        next.extras = { ...(current.extras || {}), ...v };
      } else {
        next[k] = v;
      }
    });
    next.v = STORAGE_VERSION;
    next.updatedAt = new Date().toISOString();
    if (opts.category && opts.answers) {
      next.extras = next.extras || {};
      next.extras[opts.category] = {
        ..._stripPII(opts.answers),
        updatedAt: next.updatedAt,
      };
    }
    _write(next);
    setBrief(next);
  }, []);

  /**
   * Wipe brief - call AFTER a successful sync to the server-side
   * `user_briefs` row so the user doesn't see stale anonymous data
   * after they sign in.
   */
  const clear = useCallback(() => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    const empty = _emptyBrief();
    _write(empty);
    setBrief(empty);
  }, []);

  /**
   * `true` once the user has provided meaningful brief data (more than
   * just defaults). Useful for showing "Pick up where you left off"
   * cards on the homepage.
   */
  const hasBrief = Boolean(
    brief?.updatedAt && (
      brief.build_type || brief.budget || brief.bedrooms ||
      brief.state || brief.suburb || (brief.priorities || []).length > 0
    )
  );

  return { brief, update, clear, hasBrief };
}

export default useAnonymousBrief;
